import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "@/components/lingo/DashboardLayout";
import api from "@/lib/api";
import {
  Headphones, Printer, Maximize2, Play, Pause, Volume2, BookOpen,
  Bookmark, MessageSquare, Info, Shuffle, ChevronLeft, ChevronRight, CheckCircle2, Loader2
} from "lucide-react";
import { toast } from "sonner";

export default function TestPlayer() {
  const { id } = useParams();
  const [test, setTest] = useState(null);
  const [session, setSession] = useState(null);
  const [answers, setAnswers] = useState({});
  const [secondsElapsed, setSecondsElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeQ, setActiveQ] = useState(0);
  const [tab, setTab] = useState("score");
  const [playing, setPlaying] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioCur, setAudioCur] = useState(0);
  const [audioDur, setAudioDur] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [volume, setVolume] = useState(0.85);
  const [showScript, setShowScript] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    let blobUrl = null;
    (async () => {
      const t = await api.get(`/tests/${id}`);
      setTest(t.data);
      const s = await api.post("/tests/sessions/start", { test_id: id });
      setSession(s.data);
      if (t.data.section === "LISTENING" && t.data.audio_script) {
        setAudioLoading(true);
        try {
          const r = await api.get(`/tests/${id}/audio`, { responseType: "blob" });
          blobUrl = URL.createObjectURL(r.data);
          setAudioUrl(blobUrl);
        } catch (e) {
          toast.error("Couldn't load audio — try again in a moment.");
        } finally {
          setAudioLoading(false);
        }
      }
    })();
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [id]);

  useEffect(() => {
    if (result) return;
    const t = setInterval(() => setSecondsElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [result]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
      audioRef.current.volume = volume;
    }
  }, [playbackRate, volume, audioUrl]);

  const fmtClock = (s) => `${Math.floor(s/60).toString().padStart(2,"0")}:${Math.floor(s%60).toString().padStart(2,"0")} sec`;
  const fmtAudio = (s) => {
    if (!isFinite(s) || s <= 0) return "00:00";
    return `${Math.floor(s/60).toString().padStart(2,"0")}:${Math.floor(s%60).toString().padStart(2,"0")}`;
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) audioRef.current.pause();
    else audioRef.current.play();
  };

  const seek = (e) => {
    if (!audioRef.current || !audioDur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = pct * audioDur;
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      if (test.section === "WRITING") {
        const q = test.questions[0];
        const ans = answers[q.id] || "";
        if (!ans.trim()) { toast.error("Write your answer first"); setSubmitting(false); return; }
        const ev = await api.post("/ai/evaluate-writing", { exam_type: test.exam_type, task_type: q.task_type || "TASK2", prompt: q.prompt, answer: ans });
        const r = await api.post("/tests/sessions/submit", { session_id: session.id, answers });
        setResult({ ...r.data, ai: ev.data });
      } else {
        const r = await api.post("/tests/sessions/submit", { session_id: session.id, answers });
        setResult(r.data);
      }
      toast.success("Submitted");
    } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
    finally { setSubmitting(false); }
  };

  if (!test) return <DashboardLayout title="Loading..."><div className="text-slate-400">Preparing your test…</div></DashboardLayout>;

  if (result) {
    return (
      <DashboardLayout title="Test Complete">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white border border-slate-200 rounded-3xl p-10 text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto text-[#16A34A] mb-4" />
            <div className="kicker">Your Score</div>
            <div className="font-display text-7xl mt-3 text-[#003049]">
              {result.ai?.overall_band || result.score?.band || `${result.score?.correct}/${result.score?.total}`}
            </div>
            {result.ai?.feedback && (
              <div className="mt-8 text-left">
                <div className="kicker mb-2">AI Feedback</div>
                <p className="text-slate-700 leading-relaxed">{result.ai.feedback}</p>
              </div>
            )}
            <div className="mt-10 flex gap-3 justify-center">
              <Link to="/app/tests" className="btn-outline">Back to tests</Link>
              <Link to="/app" data-testid="result-dashboard-btn" className="btn-primary">Dashboard</Link>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const numQ = test.questions.length;
  const cur = test.questions[activeQ];
  const sectionLabel = test.section === "LISTENING"
    ? `Listening - Part ${test.part || 1}`
    : test.section === "READING"
      ? `Reading - ${test.kind || "ACADEMIC"}`
      : test.title;

  return (
    <DashboardLayout hideHeader>
      <div className="flex items-center justify-between pb-3 border-b border-[#F77F00]/30">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#F77F00] text-white flex items-center justify-center">
            <Headphones className="w-4 h-4" />
          </div>
          <h1 data-testid="page-title" className="text-[#F77F00] font-display font-semibold text-lg">{sectionLabel}</h1>
        </div>
        <div className="flex items-center gap-3">
          <button className="w-8 h-8 rounded-full border border-[#F77F00] text-[#F77F00] flex items-center justify-center"><Printer className="w-3.5 h-3.5" /></button>
          <button className="w-8 h-8 text-[#F77F00]"><Maximize2 className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between bg-[#FFF4E5] border border-[#FFCDD2] rounded-2xl px-5 py-3">
        <div className="font-mono text-[#F77F00] font-medium">
          <span className="opacity-70">#{(test.id.split("-").pop() || "0001285").slice(0,12).toUpperCase()}</span>
          <span className="mx-2 opacity-40">|</span>
          {test.title.replace(/^IELTS [^:]+: ?/i, "")}
        </div>
        <div className="flex items-center gap-3 text-[#F77F00]">
          <BookOpen className="w-4 h-4" /><Bookmark className="w-4 h-4" /><MessageSquare className="w-4 h-4" /><Info className="w-4 h-4" />
        </div>
      </div>

      <div className="mt-3 text-xs text-[#F77F00]" data-testid="test-timer">
        <span className="text-slate-500">Time</span> <span className="font-medium">{fmtClock(secondsElapsed)}</span>
      </div>

      {test.section === "LISTENING" && (
        <div className="mt-3 rounded-2xl border border-[#FFCDD2] p-4 flex items-center gap-4">
          <button onClick={togglePlay} disabled={!audioReady && !audioUrl} data-testid="audio-play"
            className="w-10 h-10 rounded-full border-2 border-[#F77F00] text-[#F77F00] flex items-center justify-center disabled:opacity-50">
            {audioLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
          </button>
          <div className="text-xs font-mono text-slate-500 w-12 text-right" data-testid="audio-current">{fmtAudio(audioCur)}</div>
          <div onClick={seek} className="flex-1 h-1.5 bg-slate-100 rounded-full cursor-pointer relative">
            <div className="h-full bg-[#F77F00] rounded-full" style={{ width: audioDur ? `${(audioCur / audioDur) * 100}%` : "0%" }} />
          </div>
          <div className="text-xs font-mono text-slate-500 w-12" data-testid="audio-duration">{fmtAudio(audioDur)}</div>
          <select value={playbackRate} onChange={(e) => setPlaybackRate(parseFloat(e.target.value))} data-testid="audio-speed"
            className="text-xs border border-[#FFCDD2] text-[#F77F00] rounded-md px-2 py-1 bg-white">
            <option value={0.75}>0.75</option>
            <option value={1}>1.0</option>
            <option value={1.25}>1.25</option>
            <option value={1.5}>1.5</option>
          </select>
          <Volume2 className="w-4 h-4 text-[#F77F00]" />
          <input type="range" min="0" max="1" step="0.05" value={volume} onChange={(e) => setVolume(parseFloat(e.target.value))} data-testid="audio-volume"
            className="w-16 accent-[#F77F00]" />
        </div>
      )}

      <div className="mt-5 rounded-2xl border border-[#FFCDD2] bg-white p-6">
        {test.passage && (
          <div className="mb-5 text-slate-700 text-sm leading-relaxed">{test.passage}</div>
        )}
        <QuestionView q={cur} answers={answers} setAnswers={setAnswers} idx={activeQ} total={numQ} />
        {showScript && test.audio_script && (
          <div className="mt-4 p-4 rounded-xl bg-slate-50 text-sm text-slate-700 italic">
            <div className="kicker mb-2">Transcript</div>{test.audio_script}
          </div>
        )}
      </div>

      <div className="mt-5 rounded-2xl border border-[#FFCDD2] bg-white p-3 flex items-center justify-center gap-2 flex-wrap">
        {test.questions.map((q, i) => {
          const answered = !!answers[q.id];
          return (
            <button key={q.id} onClick={() => setActiveQ(i)} data-testid={`qnum-${i + 1}`}
              className={`w-9 h-9 rounded-full border text-sm font-medium transition-all ${
                i === activeQ ? "bg-[#F77F00] text-white border-[#F77F00]" :
                answered ? "bg-[#003049] text-white border-[#003049]" :
                "border-slate-300 text-slate-600 hover:border-[#003049]"
              }`}>{i + 1}</button>
          );
        })}
      </div>

      <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button onClick={submit} disabled={submitting} data-testid="test-submit-btn" className="px-5 py-2 rounded-full border-2 border-[#F77F00] text-[#F77F00] font-medium hover:bg-[#F77F00] hover:text-white transition-colors">
            {submitting ? "Submitting..." : "Submit"}
          </button>
          <button onClick={() => { setAnswers({}); setActiveQ(0); setSecondsElapsed(0); }} data-testid="test-restart-btn" className="px-5 py-2 rounded-full border border-[#003049] text-[#003049] font-medium hover:bg-[#003049] hover:text-white transition-colors">
            Restart
          </button>
          <button onClick={() => setShowAnswers((v) => !v)} data-testid="test-answer-btn" className="px-5 py-2 rounded-full border border-slate-300 text-slate-700 font-medium hover:border-[#003049]">
            Answer
          </button>
          <button onClick={() => setShowScript((v) => !v)} data-testid="test-script-btn" className="px-5 py-2 rounded-full border border-slate-300 text-slate-700 font-medium hover:border-[#003049]">
            Script
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button className="w-9 h-9 rounded-full border border-slate-300 text-slate-500 flex items-center justify-center"><Shuffle className="w-4 h-4" /></button>
          <button onClick={() => setActiveQ((i) => Math.max(0, i - 1))} className="w-9 h-9 rounded-full border border-slate-300 text-slate-500 flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
          <div className="px-3 py-1.5 rounded-full bg-[#F77F00] text-white font-mono text-sm">{activeQ + 1}</div>
          <div className="text-slate-600 text-sm">{numQ}</div>
          <button onClick={() => setActiveQ((i) => Math.min(numQ - 1, i + 1))} className="w-9 h-9 rounded-full bg-[#F77F00] text-white flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="mt-6 border-b border-slate-200 flex items-center gap-6 text-sm">
        <button onClick={() => setTab("score")} className={`pb-2 ${tab === "score" ? "text-[#F77F00] border-b-2 border-[#F77F00]" : "text-slate-500"}`}>My Score</button>
        <button onClick={() => setTab("community")} className={`pb-2 ${tab === "community" ? "text-[#F77F00] border-b-2 border-[#F77F00]" : "text-slate-500"}`}>Community Score</button>
      </div>
      <div className="py-12 text-center text-slate-400 text-sm">No Record Available</div>

      {showAnswers && (
        <div className="mt-4 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-sm">
          <div className="kicker text-emerald-700 mb-2">Answer Key</div>
          <ul className="space-y-1">
            {test.questions.filter((q) => q.answer).map((q, i) => (
              <li key={q.id}><span className="font-mono text-emerald-700">{i + 1}.</span> {q.answer}</li>
            ))}
          </ul>
        </div>
      )}

      {audioUrl && (
        <audio ref={audioRef} src={audioUrl}
          onLoadedMetadata={(e) => { setAudioDur(e.currentTarget.duration); setAudioReady(true); }}
          onTimeUpdate={(e) => setAudioCur(e.currentTarget.currentTime)}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          className="hidden" />
      )}
    </DashboardLayout>
  );
}

function QuestionView({ q, answers, setAnswers, idx, total }) {
  if (!q) return null;
  return (
    <div>
      <div className="text-sm text-slate-700 font-medium mb-2">Question {idx + 1} of {total}</div>
      <div className="text-base text-slate-900 mb-4">{q.prompt}</div>
      {q.type === "MCQ" && (
        <div className="space-y-2">
          {q.options?.map((o) => (
            <button key={o} data-testid={`opt-${o}`} onClick={() => setAnswers({ ...answers, [q.id]: o })}
              className={`block w-full text-left px-4 py-2.5 rounded-xl border transition-all ${
                answers[q.id] === o ? "border-[#F77F00] bg-[#FFF4E5]" : "border-slate-200 hover:border-[#003049]/40"
              }`}>{o}</button>
          ))}
        </div>
      )}
      {(q.type === "GAP_FILL" || q.type === "TRUE_FALSE_NOT_GIVEN") && (
        <input data-testid={`q-input-${q.id}`} value={answers[q.id] || ""} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
          placeholder={q.type === "TRUE_FALSE_NOT_GIVEN" ? "true / false / not given" : "Your answer"}
          className="w-full max-w-md bg-white border border-slate-200 rounded-xl px-4 py-2.5 focus:outline-none focus:border-[#F77F00]" />
      )}
      {q.type === "ESSAY" && (
        <textarea data-testid={`q-essay-${q.id}`} rows={12} value={answers[q.id] || ""} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
          placeholder="Write your essay (250+ words for Task 2)..."
          className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#F77F00]" />
      )}
    </div>
  );
}
